import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { showPromptModal } from 'src/old/actions/modal/modalActions';
import publicApiActions from 'src/old/actions/integration/publicApiActions';
import { MODAL_TYPES } from 'src/shared/components/Modals';
import { ErrorIcon } from 'src/shared/components/CustomIcons';
import { useTranslation } from 'react-i18next';
import { TextField, Button, Box, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import MailIcon from '@mui/icons-material/Mail';
import { selectCompany } from 'src/old/actions/company/companySelectors';
import contactInfo from 'src/shared/components/HelpBar/contact';
import PromptModal from '../../../../PromptModal';
import { Step } from '../../integrations2.0/components/Step';
import DeleteIcon from '@mui/icons-material/Delete';
import { useStyles, DeleteIconButton } from './styles';
import { Link as RouterLink } from 'react-router-dom';

const PublicApiForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['settings', 'common']);
  const classes = useStyles();
  const currentCompany = useSelector(selectCompany);

  const [permanentKey, setPermanentKey] = useState('');
  const [disableDelete, setDisableDelete] = useState(true);
  const [ephemeralKey, setEphemeralKey] = useState('');
  const [ephemeralExpiresAt, setEphemeralExpiresAt] = useState(null);

  useEffect(() => {
    const fetchPublicApiKey = async () => {
      const status = await publicApiActions.getPublicApiKey();
      setPermanentKey(status === 'active' ? '********************' : '');
      setDisableDelete(status !== 'active');
    };
    fetchPublicApiKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modalProps = {
    content: () => <PromptModal />,
    size: 'xs',
    onConfirm: async () => {
      const success = await publicApiActions.deletePublicApiKey(dispatch);
      if (success) {
        setPermanentKey('');
        setDisableDelete(true);
      }
    },
    icon: <ErrorIcon />,
    confirmLabel: t('confirm', { ns: 'common' })
  };

  const handleDelete = async () => {
    return showPromptModal(modalProps, MODAL_TYPES.PROMPT)(dispatch);
  };

  const handleGeneratePermanentKey = async () => {
    try {
      const apiKey = await publicApiActions.createPublicApiKey(dispatch);
      if (apiKey) {
        setPermanentKey(apiKey);
        setDisableDelete(false);
      }
    } catch (error) {}
  };

  const handleGenerateEphemeralKey = async () => {
    try {
      const result = await publicApiActions.createEphemeralApiKey(dispatch);
      if (result) {
        setEphemeralKey(result.apiKey);
        setEphemeralExpiresAt(result.expiresAt);
      }
    } catch (error) {}
  };

  return (
    <Grid2>
      <Step
        title={t('integration.to start', { ns: 'settings' })}
        subtitle={t('integrations.publicApi.view Payflow public documentation')}
        Icon={FileOpenIcon}
      >
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          component="a"
          href="https://docs.payflow.es/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('integrations.publicApi.see the documentation')}
        </Button>
      </Step>

      <Step
        title={t('integrations.step') + ' 1'}
        subtitle={t('integrations.publicApi.generate API Key')}
        Icon={VpnKeyIcon}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {t('integrations.publicApi.permanent api key')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: '26px' }}>
          {t('integrations.publicApi.permanent api key description')}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <TextField
            variant="outlined"
            label={t('integrations.publicApi.permanent api key')}
            value={permanentKey}
            inputProps={{ readOnly: true }}
            sx={{ maxWidth: '550px', width: '100%' }}
          />
          {!disableDelete && (
            <DeleteIconButton data-testid="delete-permanent-key" onClick={handleDelete}>
              <DeleteIcon />
            </DeleteIconButton>
          )}
        </Box>
        <Button variant="contained" color="primary" className={classes.button} onClick={handleGeneratePermanentKey}>
          {t('integrations.publicApi.generate permanent key')}
        </Button>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 3, mb: 0.5 }}>
          {t('integrations.publicApi.ephemeral api key')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: '26px' }}>
          {t('integrations.publicApi.ephemeral api key description')}
        </Typography>
        <Box mb={1}>
          <TextField
            variant="outlined"
            label={t('integrations.publicApi.ephemeral api key')}
            value={ephemeralKey}
            inputProps={{ readOnly: true }}
            sx={{ maxWidth: '550px', width: '100%' }}
          />
          {ephemeralExpiresAt && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
              {t('integrations.publicApi.expires at')}: {new Date(ephemeralExpiresAt).toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        <Button variant="contained" color="primary" className={classes.button} onClick={handleGenerateEphemeralKey}>
          {t('integrations.publicApi.generate ephemeral key')}
        </Button>
      </Step>

      <Step
        title={t('integrations.step') + ' 2'}
        subtitle={t('integrations.publicApi.work closely with the Payflow team to complete the integration')}
        Icon={MailIcon}
      >
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          component="a"
          href={`mailto:${contactInfo(currentCompany?.locale)?.INTEGRATION_EMAIL_ADDRESS}`}
        >
          {t('integrations.connect')}
        </Button>
      </Step>

      <Step
        title={t('integrations.step') + ' 3'}
        subtitle={t('integrations.publicApi.review the data in the view edit employees section')}
        Icon={HowToRegIcon}
      >
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          component={RouterLink}
          to="/list-employees"
        >
          {t('integrations.check')}
        </Button>
      </Step>
    </Grid2>
  );
};

export default PublicApiForm;
